import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useCustomsCases } from '../../../hooks/useCustomsCases'
import { parseCFSSWithClaude } from '../../../api/claudeCFSSParser'
import { useAuth } from '../../../contexts/AuthContext'

const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
const LABEL_CLS = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

interface FormData {
  cfss_number: string
  c18_number: string
  importer_name: string
  importer_eori: string
  agent_name: string
  initial_letter_date: string
  initial_deadline: string
  c18_letter_date: string
  c18_deadline: string
  c18_amount: string
  job_refs: string
  status: string
  appeal_reference: string
  appeal_date: string
  handed_to_agent_date: string
  ai_extracted?: boolean
  ai_confidence?: number
}

export default function NewCase() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { createCase, lookupAgentByEori } = useCustomsCases()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)
  const [dragActive, setDragActive] = useState(false)

  const [step, setStep] = useState<'upload' | 'review'>('upload')
  const [parsing, setParsing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [agentMatchStatus, setAgentMatchStatus] = useState<'idle' | 'matched' | 'not_found'>('idle')

  const [formData, setFormData] = useState<FormData>({
    cfss_number: '',
    c18_number: '',
    importer_name: '',
    importer_eori: '',
    agent_name: '',
    initial_letter_date: '',
    initial_deadline: '',
    c18_letter_date: '',
    c18_deadline: '',
    c18_amount: '',
    job_refs: '',
    status: 'open',
    appeal_reference: '',
    appeal_date: '',
    handed_to_agent_date: '',
  })

  // Auto-lookup agent when EORI changes
  useEffect(() => {
    const lookupAgent = async () => {
      if (!formData.importer_eori?.trim()) {
        setFormData(f => ({ ...f, agent_name: '' }))
        setAgentMatchStatus('idle')
        setLookupError(null)
        return
      }

      try {
        setLookupError(null)
        const agent = await lookupAgentByEori(formData.importer_eori)
        if (agent) {
          setFormData(f => ({ ...f, agent_name: agent }))
          setAgentMatchStatus('matched')
        } else {
          setFormData(f => ({ ...f, agent_name: '' }))
          setAgentMatchStatus('not_found')
        }
      } catch (e: any) {
        setLookupError(e.message)
        setAgentMatchStatus('idle')
      }
    }

    const timer = setTimeout(lookupAgent, 500)
    return () => clearTimeout(timer)
  }, [formData.importer_eori, lookupAgentByEori])

  const processFile = async (file: File) => {
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file')
      return
    }

    setParsing(true)
    setError(null)

    try {
      const reader = new FileReader()
      reader.onload = async (ev) => {
        try {
          const b64 = (ev.target?.result as string).split(',')[1]
          const extracted = await parseCFSSWithClaude(b64)

          setFormData({
            cfss_number: extracted.cfss_ref || '',
            c18_number: '',
            importer_name: extracted.importer_name || '',
            importer_eori: extracted.eori_number || '',
            agent_name: '',
            initial_letter_date: extracted.letter_date || '',
            initial_deadline: extracted.reply_deadline || '',
            c18_letter_date: '',
            c18_deadline: '',
            c18_amount: extracted.due_amount?.toString() || '',
            job_refs: '',
            status: 'open',
            appeal_reference: '',
            appeal_date: '',
            handed_to_agent_date: '',
            ai_extracted: true,
            ai_confidence: extracted.confidence,
          })

          setStep('review')
        } catch (parseErr: any) {
          setError('Failed to parse PDF: ' + parseErr.message)
        } finally {
          setParsing(false)
        }
      }
      reader.readAsDataURL(file)
    } catch (err: any) {
      setError('Error reading file: ' + err.message)
      setParsing(false)
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    await processFile(file)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current++
    setDragActive(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setDragActive(false)
    }
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    dragCounterRef.current = 0

    const files = e.dataTransfer.files
    if (!files || files.length === 0) return

    const file = files[0]
    await processFile(file)
  }

  const handleBoxClick = () => {
    if (!parsing) {
      fileInputRef.current?.click()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      const caseData = {
        cfss_number: formData.cfss_number || null,
        c18_number: formData.c18_number || null,
        importer_name: formData.importer_name || null,
        importer_eori: formData.importer_eori || null,
        agent_name: formData.agent_name || null,
        initial_letter_date: formData.initial_letter_date || null,
        initial_deadline: formData.initial_deadline || null,
        c18_letter_date: formData.c18_letter_date || null,
        c18_deadline: formData.c18_deadline || null,
        c18_amount: formData.c18_amount ? parseFloat(formData.c18_amount) : null,
        job_refs: formData.job_refs || null,
        status: formData.status,
        appeal_reference: formData.appeal_reference || null,
        appeal_date: formData.appeal_date || null,
        handed_to_agent_date: formData.handed_to_agent_date || null,
        created_by: user?.email || null,
      }

      const created = await createCase(caseData)
      navigate(`/customs/letters/${created.id}`)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleFieldChange = (key: keyof FormData, value: string) => {
    setFormData(f => ({ ...f, [key]: value }))
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Link to="/customs/letters" className="text-blue-600 hover:underline text-sm mb-4 inline-block">
        ← Back
      </Link>

      <h1 className="text-2xl font-bold mb-6">Create New CFSS Case</h1>

      {step === 'upload' && (
        <div className="bg-white rounded-lg border p-8">
          <h2 className="text-lg font-semibold mb-4">Step 1: Upload HMRC Letter (PDF)</h2>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

          <div className="mb-6">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleUpload}
              disabled={parsing}
            />
            <div
              onClick={handleBoxClick}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-500 bg-white'
              } ${parsing ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <div className="text-gray-600">
                {parsing ? (
                  <>
                    <div className="mb-2">Parsing PDF with Claude...</div>
                    <div className="inline-block animate-spin">⏳</div>
                  </>
                ) : (
                  <>
                    <div className="mb-2 font-medium">Click to upload or drag and drop</div>
                    <div className="text-sm">PDF files only</div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Link to="/customs/letters" className="px-4 py-2 border rounded hover:bg-gray-50">
              Cancel
            </Link>
            <button
              onClick={() => setStep('review')}
              className="ml-auto px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Skip & Enter Manually
            </button>
          </div>
        </div>
      )}

      {step === 'review' && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-8">
          <h2 className="text-lg font-semibold mb-4">Step 2: Review & Confirm Case Details</h2>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Left column */}
            <div>
              <label className={LABEL_CLS}>CFSS Number</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={formData.cfss_number}
                onChange={e => handleFieldChange('cfss_number', e.target.value)}
                placeholder="e.g. CFSS-10843530"
              />
            </div>

            <div>
              <label className={LABEL_CLS}>C18 Number</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={formData.c18_number}
                onChange={e => handleFieldChange('c18_number', e.target.value)}
                placeholder="e.g. C18829562"
              />
            </div>

            <div>
              <label className={LABEL_CLS}>Initial Letter Date</label>
              <input
                type="date"
                className={INPUT_CLS}
                value={formData.initial_letter_date}
                onChange={e => handleFieldChange('initial_letter_date', e.target.value)}
              />
            </div>

            <div>
              <label className={LABEL_CLS}>C18 Letter Date</label>
              <input
                type="date"
                className={INPUT_CLS}
                value={formData.c18_letter_date}
                onChange={e => handleFieldChange('c18_letter_date', e.target.value)}
              />
            </div>

            <div>
              <label className={LABEL_CLS}>Importer Name</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={formData.importer_name}
                onChange={e => handleFieldChange('importer_name', e.target.value)}
              />
            </div>

            <div>
              <label className={LABEL_CLS}>Importer EORI</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={formData.importer_eori}
                onChange={e => handleFieldChange('importer_eori', e.target.value)}
              />
              {lookupError && <div className="text-xs text-red-600 mt-1">{lookupError}</div>}
              {agentMatchStatus === 'matched' && <div className="text-xs text-green-600 mt-1">✓ Agent matched from POA</div>}
              {agentMatchStatus === 'not_found' && <div className="text-xs text-gray-500 mt-1">No matching POA found</div>}
            </div>

            <div>
              <label className={LABEL_CLS}>Initial Deadline</label>
              <input
                type="date"
                className={INPUT_CLS}
                value={formData.initial_deadline}
                onChange={e => handleFieldChange('initial_deadline', e.target.value)}
              />
            </div>

            <div>
              <label className={LABEL_CLS}>C18 Deadline</label>
              <input
                type="date"
                className={INPUT_CLS}
                value={formData.c18_deadline}
                onChange={e => handleFieldChange('c18_deadline', e.target.value)}
              />
            </div>

            <div>
              <label className={LABEL_CLS}>C18 Amount (£)</label>
              <input
                type="number"
                className={INPUT_CLS}
                value={formData.c18_amount}
                onChange={e => handleFieldChange('c18_amount', e.target.value)}
                placeholder="0.00"
                step="0.01"
              />
            </div>

            <div>
              <label className={LABEL_CLS}>Job References</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={formData.job_refs}
                onChange={e => handleFieldChange('job_refs', e.target.value)}
                placeholder="e.g. CI023942-14, CI023942-15"
              />
            </div>

            {/* Full-width fields */}
            <div className="col-span-2">
              <label className={LABEL_CLS}>Status</label>
              <select className={INPUT_CLS} value={formData.status} onChange={e => handleFieldChange('status', e.target.value)}>
                <option value="open">Open</option>
                <option value="awaiting_response">Awaiting Response</option>
                <option value="c18_received">C18 Received</option>
                <option value="appealing">Appealing</option>
                <option value="paid">Paid</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className={LABEL_CLS}>Agent Name</label>
              <input type="text" className={INPUT_CLS + ' opacity-60 cursor-not-allowed'} value={formData.agent_name} disabled placeholder="(Auto-matched from POA)" />
            </div>

            {(formData.status === 'appealing' || formData.appeal_reference) && (
              <>
                <div>
                  <label className={LABEL_CLS}>Appeal Reference (TC Code)</label>
                  <input
                    type="text"
                    className={INPUT_CLS}
                    value={formData.appeal_reference}
                    onChange={e => handleFieldChange('appeal_reference', e.target.value)}
                  />
                </div>

                <div>
                  <label className={LABEL_CLS}>Appeal Date</label>
                  <input
                    type="date"
                    className={INPUT_CLS}
                    value={formData.appeal_date}
                    onChange={e => handleFieldChange('appeal_date', e.target.value)}
                  />
                </div>

                <div className="col-span-2">
                  <label className={LABEL_CLS}>Handed to Agent Date</label>
                  <input
                    type="date"
                    className={INPUT_CLS}
                    value={formData.handed_to_agent_date}
                    onChange={e => handleFieldChange('handed_to_agent_date', e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          {formData.ai_extracted && (
            <div className="mb-6 text-xs text-gray-500">
              Extracted by Claude (confidence: {((formData.ai_confidence || 0) * 100).toFixed(0)}%)
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setStep('upload')} className="px-4 py-2 border rounded hover:bg-gray-50">
              ← Back
            </button>
            <Link to="/customs/letters" className="px-4 py-2 border rounded hover:bg-gray-50">
              Cancel
            </Link>
            <button type="submit" disabled={submitting} className="ml-auto px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {submitting ? 'Creating...' : 'Create Case'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
